
import { ChangeDetectionStrategy, Component, DestroyRef, Input, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { finalize, forkJoin, map, startWith } from 'rxjs';
import { CompanyContextService } from '@core/context/company-context.service';
import { AlertService } from '@core/services/alert.service';
import { maxDecimalsValidator } from '@core/utils/form.validators';
import { CashRegister } from '@models/cashRegister.interface';
import { ICashSession } from '@models/cashSession.interface';
import { AuthService } from '@modules/auth/auth.service';
import { ButtonComponent } from '@shared/components/form/button/button.component';
import { InputSelectComponent } from '@shared/components/form/input-select/input-select.component';
import { InputTextareaComponent } from '@shared/components/form/input-textarea/input-textarea.component';
import { ModalService } from '@core/services/modal.service';
import { SubscriptionFeaturesService } from '@core/services/subscription-features.service';
import { CashRegisterService } from '@modules/cash/cash-register/cash-register.service';
import {
  CloseCashSessionModalComponent,
  CloseCashSessionModalResult,
} from '@modules/cash/cash-session/components/close-cash-session-modal/close-cash-session-modal.component';
import { CashSessionService } from '@modules/cash/cash-session/cash-session.service';
import { CashSessionEventService } from '@core/services/cash-session-event.service';

export type OpenCashSessionModalResult = {
  type: 'success' | 'warning' | 'error';
  message: string;
  session?: ICashSession | null;
};

export type OpenCashSessionModalData = {
  onSaved?: (result: OpenCashSessionModalResult) => void;
  headerTitle?: string;
  headerDescription?: string;
  submitLabel?: string;
  cancelLabel?: string;
  cashRegisterId?: number | null;
  lockCashRegister?: boolean;
};

const OPENING_AMOUNT_MAX = 100000;

@Component({
    selector: 'app-open-cash-session-modal',
    imports: [
    ReactiveFormsModule,
    InputSelectComponent,
    InputTextareaComponent,
    ButtonComponent
],
    providers: [CashSessionService, CashRegisterService],
    templateUrl: './open-cash-session-modal.component.html',
    styleUrl: './open-cash-session-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpenCashSessionModalComponent {
  @Input() data: OpenCashSessionModalData | null = null;
  private openingContextRequestId = 0;

  private readonly destroyRef = inject(DestroyRef);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly alertService = inject(AlertService);
  private readonly authService = inject(AuthService);
  private readonly cashRegisterService = inject(CashRegisterService);
  private readonly cashSessionService = inject(CashSessionService);
  private readonly cashSessionEventService = inject(CashSessionEventService);
  private readonly modalService = inject(ModalService);
  private readonly subscriptionFeaturesService = inject(SubscriptionFeaturesService);

  readonly isSubmitting = signal(false);
  readonly cashRegisterLoadError = signal<string | null>(null);
  readonly isLoadingCashRegisters = signal(false);
  readonly isLoadingOpeningContext = signal(false);
  readonly openingContextError = signal<string | null>(null);
  readonly cashRegisters = signal<CashRegister[]>([]);
  readonly occupiedCashSessions = signal<ICashSession[]>([]);
  readonly latestClosure = signal<ICashSession | null>(null);
  readonly openingAmountMax = OPENING_AMOUNT_MAX;
  readonly cashSessionForm = new FormGroup(
    {
      cash_register_id: new FormControl<number | null>(null),
      opening_amount: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(0),
        Validators.max(OPENING_AMOUNT_MAX),
        maxDecimalsValidator(2),
      ]),
      notes: new FormControl('', [Validators.maxLength(300)]),
    },
  );
  readonly resolvedCashRegister = computed<CashRegister | null>(() => {
    const selectedId = Number(this.cashSessionForm.controls.cash_register_id.value ?? 0);

    if (selectedId > 0) {
      return this.cashRegisters().find((cashRegister) => cashRegister.id === selectedId) ?? null;
    }

    return (
      this.cashRegisters().find((cashRegister) => cashRegister.is_default && cashRegister.is_active) ??
      this.cashRegisters().find((cashRegister) => cashRegister.is_active) ??
      this.cashRegisters()[0] ??
      null
    );
  });
  readonly isBusy = computed(
    () => this.isSubmitting() || this.isLoadingOpeningContext(),
  );
  readonly hasAvailableCashRegisters = computed(() => this.cashRegisters().length > 0);
  readonly occupiedCashRegisterSummary = computed(() => {
    const occupied = this.occupiedCashSessions();

    if (!occupied.length) {
      return null;
    }

    return occupied.map((session) => this.formatOccupiedCashSession(session)).join(' ');
  });
  readonly noAvailableCashRegistersMessage = computed(() => {
    if (
      this.isLoadingCashRegisters() ||
      this.cashRegisterLoadError() ||
      this.hasAvailableCashRegisters()
    ) {
      return null;
    }

    const occupiedSummary = this.occupiedCashRegisterSummary();

    return occupiedSummary
      ? `Todas las cajas ya están ocupadas. ${occupiedSummary} Es necesario cerrar una caja para poder abrir otra. Contacta al dueño del negocio si necesitas ayuda.`
      : 'Todas las cajas ya están ocupadas. Cierra una sesión o libera una caja para continuar. Contacta al dueño del negocio si necesitas ayuda.';
  });
  readonly isAdminUser = computed(() =>
    Boolean(
      this.authService.currentUser?.rolesObject?.some(
        (role) => (role.role_key ?? role.catalogRoleObject?.role_key) === 'ADMIN',
      ),
    ),
  );
  readonly occupiedCashSessionForAdminClosure = computed(() => {
    const occupied = this.occupiedCashSessions();
    const preferredCashRegisterId = Number(this.data?.cashRegisterId ?? 0);

    if (preferredCashRegisterId > 0) {
      const preferredSession = occupied.find(
        (session) => Number(session.cash_register_id ?? 0) === preferredCashRegisterId,
      );

      if (preferredSession) {
        return preferredSession;
      }
    }

    return (
      occupied.find((session) => session.cashRegisterObject?.is_default) ??
      occupied[0] ??
      null
    );
  });
  readonly canCloseOccupiedCashSession = computed(
    () =>
      this.isAdminUser() &&
      !this.hasAvailableCashRegisters() &&
      Boolean(this.occupiedCashSessionForAdminClosure()),
  );
  readonly cashRegisterLimit = computed(() =>
    this.subscriptionFeaturesService.getLimit('cash.registers_max'),
  );
  readonly isLoadingSubscriptionFeatures = computed(() =>
    this.subscriptionFeaturesService.isLoading(),
  );
  readonly canChooseBetweenCashRegistersByPlan = computed(() => {
    const limitValue = this.cashRegisterLimit();

    return limitValue === null || limitValue === -1 || limitValue > 1;
  });
  readonly shouldShowCashRegisterSelect = computed(
    () =>
      !this.isLoadingCashRegisters() &&
      !this.isLoadingSubscriptionFeatures() &&
      this.canChooseBetweenCashRegistersByPlan() &&
      !this.data?.lockCashRegister &&
      this.cashRegisters().length > 1,
  );
  readonly cashRegisterOptions = computed(() => {
    return this.cashRegisters().map((cashRegister) => ({
      value: cashRegister.id,
      label: this.formatCashRegisterLabel(cashRegister),
    }));
  });
  readonly submitLabel = computed(
    () => this.data?.submitLabel?.trim() || 'Abrir caja',
  );
  readonly cancelLabel = computed(
    () => this.data?.cancelLabel?.trim() || 'Cancelar',
  );

  constructor() {
    this.subscriptionFeaturesService.load().subscribe({ error: () => undefined });
    this.loadOpeningContext();

    const companyId = String(this.companyContextService.companyId ?? '').trim();
    const businessId = Number(this.companyContextService.value?.business_id ?? 0);

    if (!companyId || !businessId) {
      this.cashRegisters.set([]);
      this.cashRegisterLoadError.set(null);
      this.latestClosure.set(null);
    } else {
      this.isLoadingCashRegisters.set(true);
      this.cashRegisterLoadError.set(null);

      forkJoin({
        cashRegisters: this.cashRegisterService.getAll({
          company_id: companyId,
          business_id: String(businessId),
        }),
        openSessions: this.cashSessionService
          .getAllOpenCashSessions(),
      })
        .pipe(
          finalize(() => {
            this.isLoadingCashRegisters.set(false);
          }),
        )
        .subscribe({
          next: ({ cashRegisters, openSessions }) => {
            this.occupiedCashSessions.set(openSessions);
            const occupiedCashRegisterIds = new Set(
              openSessions
                .map((session) => Number(session.cash_register_id ?? 0))
                .filter((cashRegisterId) => cashRegisterId > 0),
            );
            const availableCashRegisters = [...cashRegisters]
              .filter(
                (cashRegister) =>
                  String(cashRegister.company_id) === companyId &&
                  cashRegister.is_active &&
                  !occupiedCashRegisterIds.has(Number(cashRegister.id)),
              )
              .sort((left, right) =>
                this.formatCashRegisterLabel(left).localeCompare(
                  this.formatCashRegisterLabel(right),
                  'es',
                  { sensitivity: 'base' },
                ),
              );

            this.cashRegisters.set(availableCashRegisters);
            const preferredCashRegisterId =
              this.resolvePreferredCashRegisterId(availableCashRegisters);
            const selectedCashRegisterId = Number(
              this.cashSessionForm.controls.cash_register_id.value ?? 0,
            );
            const nextCashRegisterId = availableCashRegisters.some(
              (cashRegister) => cashRegister.id === selectedCashRegisterId,
            )
              ? selectedCashRegisterId
              : preferredCashRegisterId;

            this.cashSessionForm.controls.cash_register_id.setValue(
              nextCashRegisterId,
              { emitEvent: false },
            );

            this.loadOpeningContext();
          },
          error: (error) => {
            this.cashRegisters.set([]);
            this.occupiedCashSessions.set([]);
            this.latestClosure.set(null);
            this.cashRegisterLoadError.set(
              error?.error?.message ||
                'No fue posible cargar las cajas registradoras disponibles.',
            );
          },
        });
    }

    this.cashSessionForm.controls.cash_register_id.valueChanges
      .pipe(
        startWith(this.cashSessionForm.controls.cash_register_id.value),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.loadOpeningContext();
      });

    effect(() => {
      if (this.shouldShowCashRegisterSelect()) {
        return;
      }

      const resolvedCashRegisterId = this.resolvedCashRegister()?.id ?? null;

      if (
        this.cashSessionForm.controls.cash_register_id.value !==
        resolvedCashRegisterId
      ) {
        this.cashSessionForm.controls.cash_register_id.setValue(
          resolvedCashRegisterId,
          { emitEvent: false },
        );

        this.loadOpeningContext();
      }
    });
  }

  submit(): void {
    if (this.isBusy()) {
      return;
    }

    const resolvedCashRegister = this.resolvedCashRegister();
    const onSaved = this.data?.onSaved;

    if (!resolvedCashRegister?.id) {
      onSaved?.({
        type: 'warning',
        message: this.noAvailableCashRegistersMessage() || 'No hay cajas disponibles para abrir en este momento.',
      });
      return;
    }

    if (this.cashSessionForm.invalid) {
      this.cashSessionForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    const notes = String(this.cashSessionForm.controls.notes.value ?? '').trim();
    const basePayload = {
      cash_register_id: Number(resolvedCashRegister.id),
      ...(notes ? { notes } : {}),
    };
    const payload = {
      ...basePayload,
      opening_amount: Number(this.cashSessionForm.controls.opening_amount.value ?? 0),
    };

    this.cashSessionService
      .openCashSession(payload)
      .pipe(
        finalize(() => {
          this.isSubmitting.set(false);
        }),
      )
      .subscribe({
        next: (response) => {
          this.cashSessionEventService.notifyChanged();
          this.forceClose();
          onSaved?.({
            type: 'success',
            message: response.message || 'Caja abierta exitosamente.',
            session: response.data ?? null,
          });
        },
        error: (error) => {
          const message =
            error?.error?.message || 'No fue posible abrir la caja.';

          if (
            error?.error?.error?.code === 'CASH_SESSION_ALREADY_OPEN' ||
            error?.error?.error?.code === 'CASH_REGISTER_ALREADY_OPEN'
          ) {
            this.forceClose();
            onSaved?.({
              type: 'warning',
              message,
            });
            return;
          }

          if (error?.error?.error?.code === 'OPENING_AMOUNT_REQUIRED') {
            this.cashSessionForm.controls.opening_amount.markAsTouched();
            this.cashSessionForm.markAsTouched();
          }

          onSaved?.({
            type: 'error',
            message,
          });
        },
      });
  }

  close(): void {
    if (this.isSubmitting()) {
      return;
    }

    this.forceClose();
  }

  openCloseOccupiedCashSessionModal(): void {
    if (!this.canCloseOccupiedCashSession()) {
      return;
    }

    const session = this.occupiedCashSessionForAdminClosure();

    if (!session) {
      this.alertService.open({
        type: 'warning',
        message: 'No se encontró una caja abierta para cerrar.',
      });
      return;
    }

    this.modalService.open({
      component: CloseCashSessionModalComponent,
      title: `Cerrar caja #${session.id}`,
      porpierties: {
        width: '3xl',
        height: 'fit',
        closable: true,
      },
      data: {
        session,
        onSaved: (result: CloseCashSessionModalResult) => {
          this.alertService.open({
            type: result.type,
            message: result.message,
          });

          if (result.type !== 'error') {
            this.data?.onSaved?.(result);
          }
        },
      },
    });
  }

  private forceClose(): void {
    this.modalService.close();
  }


  get openingAmountErrorMessage(): string {
    const control = this.cashSessionForm.controls.opening_amount;

    if (!control.touched || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return 'Escribe el monto para continuar.';
    }

    if (control.errors['min']) {
      return 'El monto no puede ser negativo.';
    }

    if (control.errors['max']) {
      return `El monto inicial no puede ser mayor a ${this.formatCurrency(this.openingAmountMax)}.`;
    }

    if (control.errors['maxDecimals']) {
      return 'Usa máximo 2 decimales.';
    }

    return '';
  }

  private loadOpeningContext(): void {
    const requestId = ++this.openingContextRequestId;
    const resolvedCashRegister = this.resolvedCashRegister();

    if (!resolvedCashRegister?.id) {
      this.isLoadingOpeningContext.set(false);
      this.latestClosure.set(null);
      this.cashSessionForm.controls.opening_amount.setValue(null, { emitEvent: false });
      this.openingContextError.set(null);
      return;
    }

    this.isLoadingOpeningContext.set(true);
    this.openingContextError.set(null);

    this.cashSessionService.getCashClosures({
      page: 1,
      limit: 1,
      cash_register_id: Number(resolvedCashRegister.id),
      sortBy: 'closed_at',
      sort_order: 'DESC',
    }).pipe(
      map((response) => response.data?.data?.[0] ?? null),
      finalize(() => {
        if (requestId === this.openingContextRequestId) {
          this.isLoadingOpeningContext.set(false);
        }
      }),
    ).subscribe({
      next: (closure) => {
        if (requestId !== this.openingContextRequestId) {
          return;
        }

        this.latestClosure.set(closure);
        const latestAmount = Number(closure?.cash_left_amount ?? 0);
        this.cashSessionForm.controls.opening_amount.setValue(
          Number.isFinite(latestAmount) && latestAmount > 0 ? latestAmount : null,
          { emitEvent: false },
        );
      },
      error: (error) => {
        if (requestId !== this.openingContextRequestId) {
          return;
        }

        this.latestClosure.set(null);
        this.cashSessionForm.controls.opening_amount.setValue(null, { emitEvent: false });
        this.openingContextError.set(
          error?.error?.message ||
          'No fue posible consultar el último cierre de esta caja.',
        );
      },
    });
  }

  private formatCashRegisterLabel(
    cashRegister?: CashRegister | null,
    fallbackId?: number | null,
  ): string {
    const code = String(cashRegister?.code ?? '').trim();
    const name = String(cashRegister?.name ?? '').trim();

    if (code && name) {
      return `${code} · ${name}`;
    }

    if (code) {
      return code;
    }

    if (name) {
      return name;
    }

    const numericId = Number(cashRegister?.id ?? fallbackId ?? 0);
    return numericId ? `Caja #${numericId}` : 'Caja principal';
  }

  private formatOccupiedCashSession(session: ICashSession): string {
    const cashRegisterLabel = this.formatCashRegisterLabel(
      session.cashRegisterObject ?? null,
      session.cash_register_id ?? null,
    );
    const userLabel = this.formatUserLabel(session);

    return `${cashRegisterLabel} está abierta por ${userLabel}.`;
  }

  private formatUserLabel(session: ICashSession): string {
    const user = session.openedByUserObject;
    const fullName = [
      user?.name,
      user?.father_lastname,
      user?.mother_lastname,
    ]
      .filter(Boolean)
      .join(' ')
      .trim();

    return fullName || String(user?.email ?? '').trim() || 'otro usuario';
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value);
  }

  private resolvePreferredCashRegisterId(
    cashRegisters: CashRegister[],
  ): number | null {
    const preferredFromData = Number(this.data?.cashRegisterId ?? 0);

    if (
      preferredFromData > 0 &&
      cashRegisters.some((cashRegister) => Number(cashRegister.id) === preferredFromData)
    ) {
      return preferredFromData;
    }

    return (
      cashRegisters.find(
        (cashRegister) => cashRegister.is_default && cashRegister.is_active,
      )?.id ??
      cashRegisters.find((cashRegister) => cashRegister.is_active)?.id ??
      cashRegisters[0]?.id ??
      null
    );
  }
}
